/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 * Page Reports Constructor List Row component
 *
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.22.0
 */

import { useRef } from 'react';
import type { FC } from 'react';

import { useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';

import { EConstructorItem } from '../constants';
import { reportsConstructorCurrentRowItemSelector } from '../selector';
import type { IConstructorReport, IConstructorTemplate } from '../types';
import { isReport, isTemplate } from '../utils/tableConstructorSchedule';
import { ConstructorReportsCard } from './ConstructorReportsCard';
import ConstructorTemplateCard from './ConstructorTemplateCard';
import { ConstructorRowStyled, TableConstructorScheduleContainerStyled } from './styled/ConstructorStyled';
import { TableSchedule } from './TableConstructorSchedule/TableSchedule';

interface IProps {
    rowId: string;
    row: Array<IConstructorReport | IConstructorTemplate>;
    rowType?: EConstructorItem;
};

export const ConstructorItemsRow: FC<IProps> = ({ row, rowId, rowType },
) => {
    const currentRowItem = useSelector(reportsConstructorCurrentRowItemSelector);

    const tableContainerRef = useRef<HTMLLIElement | null>(null);

    const isOpenTable = currentRowItem?.rowId === rowId && currentRowItem.rowType === rowType;

    const rowItem = row.map((item) => {
        if (isReport(item)) {
            return (
                <ConstructorReportsCard
                    key={item.id}
                    report={item}
                    rowId={rowId}
                />
            )
        } else if (isTemplate(item)) {
            return (
                <ConstructorTemplateCard
                    key={item.id}
                    rowId={rowId}
                    template={item}
                />
            )
        } else {
            return null;
        }
    });

    return (
        <li>
            <ConstructorRowStyled>
                {rowItem}
                <CSSTransition
                    appear={true}
                    classNames='schedule-container'
                    in={isOpenTable}
                    mountOnEnter={true}
                    nodeRef={tableContainerRef}
                    timeout={{
                        enter: 500,
                        exit: 500,
                    }}
                    unmountOnExit={true}
                >
                    <TableConstructorScheduleContainerStyled
                        ref={tableContainerRef}
                    >
                        <TableSchedule />
                    </TableConstructorScheduleContainerStyled>
                </CSSTransition>
            </ConstructorRowStyled>
        </li>
    );
}
